/**
 * OverviewTab - Agent identity information for the drawer
 *
 * Displays foundational agent information:
 * - OS and OS version
 * - Architecture
 * - IP Addresses (using AgentIPList component)
 */

import { InformationCircleIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Tooltip } from '@/components/Tooltip';
import type { Agent } from '@/hooks/useAgents';
import { AgentIPList } from '@/sections/agents/components/drawer/AgentIPList';

interface OverviewTabProps {
  agent: Agent;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ agent }) => {
  return (
    <div
      className="flex flex-col gap-6"
      data-testid="agent-drawer-overview-tab"
    >
      {/* Header - Always visible */}
      <div className="flex items-center gap-2 px-6 pt-6">
        <h3 className="text-chariot-text-primary text-lg font-semibold">
          Agent Overview
        </h3>
        <Tooltip title="View agent system information and network configuration">
          <div className="text-chariot-text-secondary cursor-help">
            <InformationCircleIcon className="size-4" />
          </div>
        </Tooltip>
      </div>

      {/* System Information */}
      {(agent.os || agent.architecture) && (
        <section className="bg-chariot-background-secondary flex flex-col gap-2 rounded-md p-6">
          <h3 className="text-chariot-text-primary text-sm font-medium">
            System Information
          </h3>
          <div className="text-chariot-text-secondary flex flex-col gap-1.5 text-sm">
            {agent.os && (
              <div>
                <span className="text-chariot-text-primary font-medium">
                  OS:
                </span>{' '}
                {agent.os}
                {agent.os_version && ` ${agent.os_version}`}
              </div>
            )}
            {agent.architecture && (
              <div>
                <span className="text-chariot-text-primary font-medium">
                  Architecture:
                </span>{' '}
                {agent.architecture}
              </div>
            )}
          </div>
        </section>
      )}

      {/* IP Addresses */}
      <AgentIPList
        className="bg-chariot-background-secondary rounded-md p-6"
        ips={
          agent.network_interfaces?.flatMap(iface => iface.ip_addresses) ?? []
        }
      />
    </div>
  );
};
